<template>
  <AuthGuard>
    <v-container class="app-page-content pa-4 pa-md-6">
      <PageHeader title="Profile" :showBack="true" back-to="/customers">
        <v-btn color="primary" @click="openDialog(null)">New Prompt</v-btn>
      </PageHeader>

      <!-- Prompts List -->
      <v-card class="profile-prompts-shell mb-6 pa-4" rounded="lg" elevation="0" border>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            lg="4"
            v-for="prompt in [
              { id: 'default', title: 'System Default', content: reportStore.getPrompt() },
              ...profileStore.prompts
            ]"
            :key="prompt.id"
          >
            <v-card
              class="profile-prompt-card cursor-pointer position-relative"
              rounded="lg"
              elevation="0"
              border
              variant="outlined"
              @click="openDialog(prompt)"
            >
              <v-icon
                v-if="prompt.id === 'default'"
                class="position-absolute top-0 start-0 ma-2 text-medium-emphasis"
                size="20"
              >
                mdi-lock-outline
              </v-icon>

              <v-card-title
                class="text-body-1 font-weight-bold"
                :class="prompt.id === 'default' ? 'pe-8' : ''"
              >
                {{ prompt.title }}
              </v-card-title>
              <v-card-text class="profile-prompt-card__body text-body-2 text-medium-emphasis">
                <div class="profile-prompt-card__preview">
                  {{ prompt.content }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card>

      <PromptDialog v-model="dialog" :prompt="selectedPrompt" />
    </v-container>
  </AuthGuard>
</template>

<script setup lang="ts">
import PageHeader from "@/components/PageHeader.vue";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import PromptDialog from "@/components/Widgets/PromptDialog.vue";
import { useProfileStore } from "@/stores/profile";
import { useReportStore } from "@/stores/report";
import type { IApp } from "@/types";
import { ref } from "vue";

const profileStore = useProfileStore();
const reportStore = useReportStore();
const dialog = ref(false);
const selectedPrompt = ref<IApp.IPrompt | null>(null);
const isSetDefaultLoading = ref(false);
const savingId = ref<string | null>(null);

function openDialog(prompt: IApp.IPrompt | null) {
  selectedPrompt.value = prompt;
  dialog.value = true;
}
async function setAsDefault(prompt: IApp.IPrompt) {
  isSetDefaultLoading.value = true;
  savingId.value = prompt.id;
  if (prompt.id === "default") {
    await profileStore.setDefaultPrompt(null);
  } else {
    await profileStore.setDefaultPrompt(prompt.id);
  }
  savingId.value = null;
  isSetDefaultLoading.value = false;
}
</script>

<style scoped>
.profile-prompts-shell {
  background: rgb(var(--v-theme-surface));
}

.profile-prompt-card {
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.profile-prompt-card:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
}

.profile-prompt-card :deep(.v-card-title) {
  padding-top: 14px;
  padding-bottom: 4px;
  line-height: 1.35;
}

/* Padding lives on the body; preview is a fixed-height inner clip so bottom padding is visible */
.profile-prompt-card :deep(.profile-prompt-card__body) {
  padding-top: 0;
  padding-bottom: 24px;
  padding-left: 16px;
  padding-right: 16px;
}

.profile-prompt-card__preview {
  height: 5rem;
  max-height: 5rem;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
